import React from 'react';
import classNames from 'classnames';

import { Icon } from 'ui';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<Size, string> = {
  sm: 'h-16 w-16',
  md: 'h-20 w-20',
  lg: 'h-32 w-32',
  xl: 'h-40 w-40',
};

const ICON_SIZE: Record<Size, 'large' | 'huge'> = {
  sm: 'large',
  md: 'large',
  lg: 'huge',
  xl: 'huge',
};

type Props = {
  src?: string | null;
  alt?: string | null;
  size?: Size;
  className?: string;
};

/**
 * Renders a branch photo with consistent sizing, rounded corners and a fallback icon.
 *
 * Applies an image-rendering trick that forces GPU compositing on the <img>, which avoids
 * the blocky/pixelated downscale artifacts seen in some browsers when shrinking large photos.
 */
function BranchPhoto({ src, alt, size = 'md', className }: Props) {
  const sizeClass = SIZE_CLASS[size];
  if (src) {
    return (
      <img
        src={src}
        alt={alt ?? ''}
        loading="lazy"
        decoding="async"
        className={classNames(
          sizeClass,
          'flex-shrink-0 rounded-lg border border-gray-200 object-cover',
          '[image-rendering:auto] [-webkit-backface-visibility:hidden] [transform:translateZ(0)]',
          className,
        )}
      />
    );
  }
  return (
    <div
      className={classNames(
        sizeClass,
        'flex flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400',
        className,
      )}
    >
      <Icon name="home" size={ICON_SIZE[size]} fitted solid />
    </div>
  );
}

export default BranchPhoto;
